import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/shared/UI";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, X, Bell, BellOff } from "lucide-react";

export function ChatManageDialog({ open, chat, onClose, onChanged }) {
  const { user, users } = useAuth();
  const [name, setName] = useState(chat?.name || "");
  const [busy, setBusy] = useState(false);
  if (!chat) return null;
  const memberIds = chat.member_ids || [];
  const candidates = (users || []).filter((u) => !memberIds.includes(u.id));

  const rename = async () => {
    if (!name.trim() || name.trim() === chat.name) return;
    setBusy(true);
    try { const { data } = await client.patch(`/chats/${chat.id}`, { name: name.trim() }); onChanged && onChanged(data); toast.success("Renamed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const addMember = async (uid) => {
    try { const { data } = await client.post(`/chats/${chat.id}/members`, { member_ids: [uid] }); onChanged && onChanged(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const removeMember = async (uid) => {
    try { const { data } = await client.delete(`/chats/${chat.id}/members/${uid}`); onChanged && onChanged(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const setArchive = async (payload, msg) => {
    try { const { data } = await client.patch(`/chats/${chat.id}`, payload); onChanged && onChanged(data); toast.success(msg); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggleMute = async () => {
    try { const { data } = await client.patch(`/chats/${chat.id}`, { muted: !chat.muted }); onChanged && onChanged(data); toast.success(data.muted ? "Notifications muted" : "Notifications unmuted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="chat-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Chat settings</DialogTitle>
          <DialogDescription className="sr-only">Manage members and settings for this chat</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">Chat name</label>
            <div className="flex gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="chat-rename-input"
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100" />
              <Button onClick={rename} disabled={busy} variant="outline" data-testid="chat-rename-save">Save</Button>
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold text-slate-600">Members ({memberIds.length})</p>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-100 p-1">
              {(chat.members || []).map((m) => (
                <div key={m.id} className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm" data-testid={`chat-member-${m.id}`}>
                  <Avatar name={m.name} color={m.color} className="h-7 w-7 text-[11px]" />
                  <span className="flex-1 truncate text-slate-700">{m.name}{m.id === user?.id ? " (you)" : ""}</span>
                  {memberIds.length > 1 && (
                    <button onClick={() => removeMember(m.id)} data-testid={`chat-member-remove-${m.id}`} className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-600"><UserPlus className="h-3.5 w-3.5" />Add people</p>
            {candidates.length === 0 ? (
              <p className="rounded-lg border border-slate-100 px-3 py-2 text-xs text-slate-400">Everyone is already in this chat.</p>
            ) : (
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-100 p-1">
                {candidates.map((u) => (
                  <div key={u.id} className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                    <Avatar name={u.name} color={u.color} className="h-7 w-7 text-[11px]" />
                    <span className="flex-1 truncate text-slate-700">{u.name}</span>
                    <button onClick={() => addMember(u.id)} data-testid={`chat-member-add-${u.id}`} className="rounded-md px-2 py-1 text-xs font-semibold text-sky-600 hover:bg-sky-50">Add</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-3">
            <div>
              <p className="text-xs font-semibold text-slate-600">Notifications</p>
              <p className="text-[11px] text-slate-400">{chat.muted ? "Muted — no alerts or unread badges" : "On — you'll get alerts for new messages"}</p>
            </div>
            <button onClick={toggleMute} data-testid="chat-mute-toggle"
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
              {chat.muted ? <><Bell className="h-3.5 w-3.5" />Unmute</> : <><BellOff className="h-3.5 w-3.5" />Mute</>}
            </button>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-600">Archive</p>
            <div className="flex flex-wrap gap-2">
              {chat.archived_everyone ? (
                <button onClick={() => setArchive({ archived_everyone: false }, "Chat restored for everyone")} data-testid="chat-archive-everyone-toggle"
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Restore for everyone</button>
              ) : (
                <>
                  <button onClick={() => setArchive({ archived: !chat.archived }, chat.archived ? "Chat restored" : "Chat archived for you")} data-testid="chat-archive-me-toggle"
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                    {chat.archived ? "Restore for me" : "Archive for me"}
                  </button>
                  <button onClick={() => setArchive({ archived_everyone: true }, "Chat archived for everyone")} data-testid="chat-archive-everyone-toggle"
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100">
                    Archive for everyone
                  </button>
                </>
              )}
            </div>
            {chat.archived_everyone && <p className="text-[11px] text-slate-400">This chat is archived for everyone.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
